/**
 * Gamma exposure levels, as the engine converts them.
 *
 * The engine does the arithmetic AND the index->future conversion, so every
 * price here is already in futures points and can be plotted directly. Never
 * re-scale these client-side: the conversion needs the index spot and the live
 * future price sampled at the same moment, and the browser has neither.
 *
 * `available: false` means do not draw. It is not an error state to paper over
 * — the engine reports it when it has no trustworthy ratio, and drawing the raw
 * index levels instead would put every line tens of points out.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { normalizeToBase } from '../utils/symbolUtils';
import { getApiBaseUrl } from '../config/ports';

/**
 * Index option chains the engine converts. NDX -> NQ/MNQ, SPX -> ES/MES.
 * A chart only ever shows the levels of ITS underlying: NDX walls on an ES
 * chart would be ~22,000 points away, SPX walls on NQ the same the other way.
 */
export type GexUnderlying = 'NDX' | 'SPX';
export const GEX_UNDERLYINGS: GexUnderlying[] = ['NDX', 'SPX'];

/** The index whose options price a chart's levels, or null for symbols with none. */
export function gexUnderlyingForSymbol(symbol: string | null | undefined): GexUnderlying | null {
  if (!symbol) return null;
  switch (normalizeToBase(symbol)) {
    case 'NQ':
    case 'MNQ':
      return 'NDX';
    case 'ES':
    case 'MES':
      return 'SPX';
    default:
      return null;
  }
}

/**
 * Which underlying a socket/REST payload is for. Engines before multi-underlying
 * support sent NDX only, with no `underlying` on the payload; the levels' own
 * `underlying` field is the next best witness. Anything unrecognised is dropped
 * (null) rather than guessed, so it can never land on the wrong chart.
 */
export function payloadUnderlying(payload: { underlying?: unknown; levels?: { underlying?: unknown } | null } | null | undefined): GexUnderlying | null {
  const raw = payload?.underlying ?? payload?.levels?.underlying ?? 'NDX';
  const u = typeof raw === 'string' ? raw.toUpperCase() : '';
  return (GEX_UNDERLYINGS as string[]).includes(u) ? (u as GexUnderlying) : null;
}

export interface GexStrike {
  strike: number;
  /** Dollars of dealer gamma per 1% move. Sign carries direction. */
  gex: number;
}

export interface GexTermBucket {
  label: string;
  netGex: number;
  netDex: number;
  contracts: number;
}

export interface GexLevels {
  underlying: string;
  /** Future price the levels were converted against. */
  spot: number;
  chainTime: number;
  fetchedAt: number;
  netGex: number;
  /**
   * Net dealer delta. Sign is meaningful: negative = dealers lean short.
   * Optional because chains cached or emitted before the engine build that
   * added it have neither this nor `termStructure`.
   */
  netDex?: number;
  /** Exposure sliced by time to expiry; near-dated drives intraday hedging. */
  termStructure?: GexTermBucket[];
  /** Walls over the selected expiry window / method (see `selection`). */
  callWall: number | null;
  putWall: number | null;
  /** Zero gamma over the same window / method. */
  gammaFlip: number | null;
  strikes: GexStrike[];
  contractsUsed: number;
  expiriesUsed: number;
  // Optional: absent on payloads from engines before matched-time conversion.
  /** Moment the chain's quotes describe (ms): CBOE stamp minus its 15-min delay. */
  quoteTime?: number;
  futureAtQuote?: number | null;
  ratio?: number | null;
  /** Walls over every expiry (the "All expiries" view). */
  callWallAll?: number | null;
  putWallAll?: number | null;
  /** Previous zero-gamma definition (expiries within 45 days). */
  gammaFlip45d?: number | null;
  nearExpiries?: string[];
  atmIv?: number | null;
  ivExpiry?: string | null;
  expectedMove?: number | null;
  ivUpper?: number | null;
  ivLower?: number | null;
  // Optional: absent on payloads from engines before server-side expiry windows.
  /** Which window/method produced the top-level walls, zero gamma and IV bands. */
  selection?: GexSelection;
  /** Walls and zero gamma per expiry window, in futures points. */
  windows?: Record<string, GexWindowLevels>;
  ivBands?: GexIvBands | null;
}

export type GexMethod = 'calibrated' | 'legacy';

export interface GexSelection {
  expiryWindow: string;
  expiryWindowLabel: string;
  method: GexMethod;
  /** How the CALL wall was picked, e.g. "net GEX by strike, OI + volume" (and the put wall on older engines). */
  walls: string;
  zeroGamma: string;
  ivBands: string;
  /** Expiries (YYMMDD) inside the window. */
  expiries: string[];
  // Optional: absent on engines before the per-level put wall setting.
  /** How the put wall was picked, e.g. "net GEX by strike, OI only". */
  putWall?: string;
  /** 'oi' | 'calibrated' (calibrated method), 'legacy' under the legacy method. */
  putWallMethod?: string;
  /** Expiries behind the put wall, only when they differ from `expiries`. */
  putWallExpiries?: string[];
  /** IV band convention keys (calibrated method only). */
  ivHorizon?: string;
  ivAnchor?: string;
  ivSource?: string;
}

export interface GexWindowLevels {
  expiries: string[];
  contracts: number;
  callWall: number | null;
  putWall: number | null;
  gammaFlip: number | null;
  legacyCallWall: number | null;
  legacyPutWall: number | null;
  legacyGammaFlip: number | null;
}

export interface GexIvBands {
  iv: number;
  source: string;
  movePts: number;
  upper: number;
  lower: number;
  /** Which anchor the band is centred on ('spot' | 'open' | 'prevclose'), its price, and the horizon key. */
  anchor?: string;
  anchorPrice?: number;
  horizon?: string;
}

/** Server-side level settings for one underlying. */
export interface GexUnderlyingSettings {
  expiryWindow: string;
  method: string;
  /** Calibrated method only: which book draws the put wall. */
  putWallMethod: string;
  /** Calibrated method only: the IV band convention. */
  ivHorizon: string;
  ivAnchor: string;
  ivSource: string;
}

/** The per-level settings beyond window + method, each with its option list key in the payload. */
export type GexConventionKey = 'putWallMethod' | 'ivHorizon' | 'ivAnchor' | 'ivSource';

export interface GexOption {
  key: string;
  label: string;
}

/** Shown before GET /api/gex/settings answers (and if it never does). */
export const GEX_WINDOW_OPTIONS: GexOption[] = [
  { key: '0dte', label: '0DTE only' },
  { key: '0-2dte', label: '0-2 DTE' },
  { key: 'near', label: 'Nearest + next weekly' },
  { key: '7d', label: '<= 7 days' },
  { key: '14d', label: '<= 14 days' },
  { key: '30d', label: '<= 30 days' },
  { key: '45d', label: '<= 45 days' },
  { key: 'all', label: 'All expiries' },
];

export const GEX_METHOD_OPTIONS: GexOption[] = [
  { key: 'calibrated', label: 'Calibrated (OI + volume)' },
  { key: 'legacy', label: 'Legacy (OI only)' },
];

export const GEX_PUT_WALL_OPTIONS: GexOption[] = [
  { key: 'oi', label: 'OI only' },
  { key: 'calibrated', label: 'Calibrated (OI + volume)' },
];

export const GEX_IV_HORIZON_OPTIONS: GexOption[] = [
  { key: 'rest', label: 'Rest of session' },
  { key: '1d', label: '1 trading day' },
  { key: '1h', label: '1 hour' },
  { key: '30m', label: '30 minutes' },
  { key: 'nexthour', label: 'To the next hour' },
];

export const GEX_IV_ANCHOR_OPTIONS: GexOption[] = [
  { key: 'spot', label: 'Current spot' },
  { key: 'open', label: 'Session open' },
  { key: 'prevclose', label: 'Prior close' },
];

export const GEX_IV_SOURCE_OPTIONS: GexOption[] = [
  { key: 'iv30', label: 'iv30 (live)' },
  { key: 'iv30close', label: 'iv30 at the prior close' },
  { key: 'atm', label: 'ATM IV, nearest expiry' },
];

export const DEFAULT_GEX_EXPIRY_WINDOW = '0-2dte';
export const DEFAULT_GEX_METHOD = 'calibrated';

/** Defaults of the per-level settings (the 2026-10-13 calibration). */
export const DEFAULT_GEX_CONVENTIONS: Record<GexConventionKey, string> = {
  putWallMethod: 'oi',
  ivHorizon: '1h',
  ivAnchor: 'open',
  ivSource: 'iv30close',
};

/** Fallback option list per convention setting. */
export const GEX_CONVENTION_OPTIONS: Record<GexConventionKey, GexOption[]> = {
  putWallMethod: GEX_PUT_WALL_OPTIONS,
  ivHorizon: GEX_IV_HORIZON_OPTIONS,
  ivAnchor: GEX_IV_ANCHOR_OPTIONS,
  ivSource: GEX_IV_SOURCE_OPTIONS,
};

/** Payload key of each convention setting's option list. */
const CONVENTION_PAYLOAD_KEY: Record<GexConventionKey, keyof GexSettingsPayload> = {
  putWallMethod: 'putWallMethods',
  ivHorizon: 'ivHorizons',
  ivAnchor: 'ivAnchors',
  ivSource: 'ivSources',
};

export const GEX_CONVENTION_KEYS: GexConventionKey[] = ['putWallMethod', 'ivHorizon', 'ivAnchor', 'ivSource'];

/** Human label for an expiry-window key (the key itself when unknown). */
export function expiryWindowLabel(key: string | null | undefined, options: GexOption[] = GEX_WINDOW_OPTIONS): string {
  if (!key) return '';
  return options.find((o) => o.key === key)?.label ?? GEX_WINDOW_OPTIONS.find((o) => o.key === key)?.label ?? key;
}

/** Body of GET/POST /api/gex/settings and the `gex:settings` socket event. */
export interface GexSettingsPayload {
  settings?: Partial<Record<string, Partial<GexUnderlyingSettings>>>;
  windows?: GexOption[];
  methods?: GexOption[];
  putWallMethods?: GexOption[];
  ivHorizons?: GexOption[];
  ivAnchors?: GexOption[];
  ivSources?: GexOption[];
  defaults?: Partial<GexUnderlyingSettings>;
}

/** Which GEX elements to draw. Mirrors how levelsStore models visibility. */
export type GexElement = 'callWall' | 'putWall' | 'gammaFlip' | 'ivBands' | 'strikes';

export const GEX_ELEMENTS: GexElement[] = ['callWall', 'putWall', 'gammaFlip', 'ivBands', 'strikes'];

export const GEX_ELEMENT_LABELS: Record<GexElement, { label: string; description: string }> = {
  callWall: { label: 'Call Wall', description: 'Biggest call-gamma strike above spot - tends to cap rallies' },
  putWall: { label: 'Put Wall', description: 'Biggest put-gamma strike below spot - often support' },
  gammaFlip: { label: 'Zero Gamma', description: 'Where dealer gamma flips sign - regime boundary' },
  ivBands: { label: 'IV+ / IV-', description: 'Options-implied expected range (convention set per index under Chain: anchor, IV source, horizon)' },
  strikes: { label: 'Gamma Ladder', description: 'Largest strikes by absolute gamma exposure' },
};

/** What the engine last said about one underlying. */
export interface GexSlot {
  levels: GexLevels | null;
  /** False means the engine could not produce trustworthy levels. */
  available: boolean;
  reason: string | null;
}

export const EMPTY_GEX_SLOT: GexSlot = Object.freeze({ levels: null, available: false, reason: null }) as GexSlot;

interface GexState {
  /** Per underlying; a missing entry means nothing has arrived yet. */
  byUnderlying: Partial<Record<GexUnderlying, GexSlot>>;

  visibility: Record<GexElement, boolean>;
  /** How many ladder strikes to draw. The full set is visual noise. */
  ladderCount: number;

  /** Server-side expiry window + method per underlying (not persisted: the engine owns it). */
  settings: Partial<Record<GexUnderlying, GexUnderlyingSettings>>;
  windowOptions: GexOption[];
  methodOptions: GexOption[];
  /** Option lists of the per-level settings (server-provided, fallbacks above). */
  conventionOptions: Record<GexConventionKey, GexOption[]>;
  settingsError: string | null;
  savingSettings: Partial<Record<GexUnderlying, boolean>>;

  setLevels: (underlying: GexUnderlying, levels: GexLevels) => void;
  setUnavailable: (underlying: GexUnderlying, reason: string) => void;
  toggleElement: (el: GexElement) => void;
  setLadderCount: (n: number) => void;
  fetchSettings: () => Promise<void>;
  setExpiryWindow: (underlying: GexUnderlying, key: string) => Promise<void>;
  setMethod: (underlying: GexUnderlying, key: string) => Promise<void>;
  /** Put wall method or an IV band convention field. */
  setConvention: (underlying: GexUnderlying, field: GexConventionKey, key: string) => Promise<void>;
  applySettingsPayload: (payload: unknown) => void;
}

const DEFAULT_GEX_VISIBILITY: Record<GexElement, boolean> = {
  callWall: true,
  putWall: true,
  gammaFlip: true,
  ivBands: true,
  // Off by default: the walls and zero gamma are the decision-relevant
  // levels, the ladder is context and clutters a 2-minute chart.
  strikes: false,
};

/**
 * Persisted GEX prefs merged per key, so an element added after a user's
 * prefs were saved (ivBands) starts at its default instead of `undefined`
 * (= hidden) - the shallow default merge would replace the whole object.
 */
export function mergePersistedGex<T extends { visibility: Record<GexElement, boolean> }>(
  persisted: unknown,
  current: T,
): T {
  const p = (persisted && typeof persisted === 'object' ? persisted : {}) as Record<string, unknown>;
  const visibility = { ...DEFAULT_GEX_VISIBILITY, ...current.visibility };
  const saved = p.visibility;
  if (saved && typeof saved === 'object') {
    for (const key of GEX_ELEMENTS) {
      const v = (saved as Record<string, unknown>)[key];
      if (typeof v === 'boolean') visibility[key] = v;
    }
  }
  // A `wallScope` saved by older bundles is ignored: the expiry window is a
  // server-side setting now.
  const ladderCount = typeof p.ladderCount === 'number' ? p.ladderCount : (current as { ladderCount?: number }).ladderCount;
  return { ...current, visibility, ...(ladderCount !== undefined ? { ladderCount } : {}) };
}

const isOptionList = (v: unknown): v is GexOption[] =>
  Array.isArray(v) && v.length > 0 && v.every((o) => o && typeof o === 'object'
    && typeof (o as GexOption).key === 'string' && typeof (o as GexOption).label === 'string');

/**
 * State patch for a settings payload (GET/POST body or `gex:settings`).
 * Defensive: anything malformed is skipped rather than blanking what we have,
 * and only NDX/SPX entries are taken.
 */
export function parseSettingsPayload(
  payload: unknown,
  s: Pick<GexState, 'settings' | 'windowOptions' | 'methodOptions'> & Partial<Pick<GexState, 'conventionOptions'>>,
): Pick<GexState, 'settings' | 'windowOptions' | 'methodOptions' | 'conventionOptions'> {
  const p = (payload && typeof payload === 'object' ? payload : {}) as GexSettingsPayload;
  const settings = { ...s.settings };
  const raw = p.settings && typeof p.settings === 'object' ? p.settings : {};
  for (const u of GEX_UNDERLYINGS) {
    const v = raw[u];
    if (!v || typeof v !== 'object') continue;
    const prev = settings[u];
    const expiryWindow = typeof v.expiryWindow === 'string' ? v.expiryWindow : prev?.expiryWindow ?? DEFAULT_GEX_EXPIRY_WINDOW;
    const method = typeof v.method === 'string' ? v.method : prev?.method ?? DEFAULT_GEX_METHOD;
    const conv = {} as Record<GexConventionKey, string>;
    for (const k of GEX_CONVENTION_KEYS) {
      const val = v[k];
      conv[k] = typeof val === 'string' ? val : prev?.[k] ?? DEFAULT_GEX_CONVENTIONS[k];
    }
    settings[u] = { expiryWindow, method, ...conv };
  }
  const conventionOptions = { ...(s.conventionOptions ?? GEX_CONVENTION_OPTIONS) };
  for (const k of GEX_CONVENTION_KEYS) {
    const list = p[CONVENTION_PAYLOAD_KEY[k]];
    if (isOptionList(list)) conventionOptions[k] = list;
  }
  return {
    settings,
    conventionOptions,
    windowOptions: isOptionList(p.windows) ? p.windows : s.windowOptions,
    methodOptions: isOptionList(p.methods) ? p.methods : s.methodOptions,
  };
}

/** The slot for one underlying (a stable empty slot when there is none). */
export const selectGexSlot = (u: GexUnderlying | null) => (s: { byUnderlying: Partial<Record<GexUnderlying, GexSlot>> }): GexSlot =>
  (u && s.byUnderlying[u]) || EMPTY_GEX_SLOT;

export const useGexStore = create<GexState>()(
  persist(
    (set, get) => {
      /**
       * Optimistic: the select moves at once; a failed POST puts the previous
       * value back and says why. A success applies the engine's answer, which
       * is the truth (it may normalise the value).
       */
      // Per-index request sequence: only the LATEST POST may apply its answer,
      // clear the saving flag or revert, so two quick changes cannot undo each
      // other. `confirmed` is the last value the engine acknowledged, which is
      // what a failed latest request reverts to.
      const seq: Partial<Record<GexUnderlying, number>> = {};
      const confirmed: Partial<Record<GexUnderlying, GexUnderlyingSettings | undefined>> = {};
      const postSettings = async (underlying: GexUnderlying, patch: Partial<GexUnderlyingSettings>) => {
        const mine = (seq[underlying] ?? 0) + 1;
        seq[underlying] = mine;
        if (!get().savingSettings[underlying]) confirmed[underlying] = get().settings[underlying];
        const current = get().settings[underlying];
        const sel = get().byUnderlying[underlying]?.levels?.selection;
        const base: GexUnderlyingSettings = current ?? {
          expiryWindow: sel?.expiryWindow ?? DEFAULT_GEX_EXPIRY_WINDOW,
          method: sel?.method ?? DEFAULT_GEX_METHOD,
          putWallMethod: sel?.putWallMethod && sel.putWallMethod !== 'legacy' ? sel.putWallMethod : DEFAULT_GEX_CONVENTIONS.putWallMethod,
          ivHorizon: sel?.ivHorizon || DEFAULT_GEX_CONVENTIONS.ivHorizon,
          ivAnchor: sel?.ivAnchor || DEFAULT_GEX_CONVENTIONS.ivAnchor,
          ivSource: sel?.ivSource || DEFAULT_GEX_CONVENTIONS.ivSource,
        };
        set((s) => ({
          settings: { ...s.settings, [underlying]: { ...base, ...patch } },
          savingSettings: { ...s.savingSettings, [underlying]: true },
          settingsError: null,
        }));
        const isLatest = () => seq[underlying] === mine;
        const revert = (msg: string) => {
          if (!isLatest()) return;
          // Read at revert time: an older request may have been saved since.
          const before = confirmed[underlying];
          set((s) => {
            const settings = { ...s.settings };
            if (before) settings[underlying] = before;
            else delete settings[underlying];
            return { settings, settingsError: msg, savingSettings: { ...s.savingSettings, [underlying]: false } };
          });
        };
        try {
          const r = await fetch(`${getApiBaseUrl()}/gex/settings`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ underlying, ...patch }),
          });
          let body: unknown = null;
          try { body = await r.json(); } catch { /* non-JSON error body */ }
          if (!r.ok) {
            const err = body && typeof body === 'object' ? (body as { error?: unknown }).error : undefined;
            revert(`${underlying}: ${typeof err === 'string' && err ? err : `save failed (HTTP ${r.status})`}`);
            return;
          }
          if (!isLatest()) {
            // A newer change is in flight; its answer wins. But this one is
            // saved on the engine, so it is what a failure of the newer one
            // must revert to (several selects can be changed in one round trip).
            const answered = parseSettingsPayload(body, get()).settings[underlying];
            if (answered) confirmed[underlying] = answered;
            return;
          }
          set((s) => ({ ...parseSettingsPayload(body, s), savingSettings: { ...s.savingSettings, [underlying]: false } }));
          confirmed[underlying] = get().settings[underlying];
        } catch (e) {
          revert(`${underlying}: save failed (${e instanceof Error ? e.message : 'network error'})`);
        }
      };

      return {
      byUnderlying: {},

      visibility: { ...DEFAULT_GEX_VISIBILITY },
      ladderCount: 6,
      settings: {},
      windowOptions: GEX_WINDOW_OPTIONS,
      methodOptions: GEX_METHOD_OPTIONS,
      conventionOptions: GEX_CONVENTION_OPTIONS,
      settingsError: null,
      savingSettings: {},

      setLevels: (underlying, levels) =>
        set((s) => ({ byUnderlying: { ...s.byUnderlying, [underlying]: { levels, available: true, reason: null } } })),

      // Deliberately keeps the last known levels. The engine says "unavailable"
      // when its ratio is untrustworthy, which is usually transient (no live
      // price yet after a restart); blanking the chart on every hiccup is worse
      // than briefly showing the previous set, and `available` records the truth.
      setUnavailable: (underlying, reason) =>
        set((s) => ({
          byUnderlying: {
            ...s.byUnderlying,
            [underlying]: { levels: s.byUnderlying[underlying]?.levels ?? null, available: false, reason },
          },
        })),

      toggleElement: (el) =>
        set((s) => ({ visibility: { ...s.visibility, [el]: !s.visibility[el] } })),

      setLadderCount: (n) => set({ ladderCount: Math.max(0, Math.min(12, n)) }),

      applySettingsPayload: (payload) => set((s) => parseSettingsPayload(payload, s)),

      fetchSettings: async () => {
        try {
          const r = await fetch(`${getApiBaseUrl()}/gex/settings`);
          if (!r.ok) return; // older engine without settings: keep the fallbacks
          get().applySettingsPayload(await r.json());
        } catch {
          /* engine may be booting; `gex:settings` will fill it in */
        }
      },

      setExpiryWindow: (underlying, key) => postSettings(underlying, { expiryWindow: key }),
      setMethod: (underlying, key) => postSettings(underlying, { method: key }),
      setConvention: (underlying, field, key) => postSettings(underlying, { [field]: key }),
      };
    },
    {
      name: 'gex-config',
      // Only the user's display choices survive a reload. Levels themselves are
      // market data with a timestamp — restoring a stale chain from localStorage
      // would draw yesterday's gamma as though it were current.
      partialize: (s) => ({ visibility: s.visibility, ladderCount: s.ladderCount }),
      merge: (persisted, current) => mergePersistedGex(persisted, current),
    },
  ),
);
